import React, { useCallback, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { t } from '../../utils/i18n';
import {
  getGrowthDateRange, growthChartRows, growthViewSearch, readGrowthView,
  metricNumber, queryStatus, STORE_METRICS, storeMetric, validateGrowthRange,
} from '../../utils/growthAnalytics';
import DashboardKpi from '../Dashboard/components/DashboardKpi';
import DashboardPanel from '../Dashboard/components/DashboardPanel';
import GaDateRangeControls from '../Dashboard/ga/GaDateRangeControls';
import GaMetricLineChart from '../Dashboard/ga/GaMetricLineChart';
import useGrowthData from './useGrowthData';
import QueryStatus from '../../ui/QueryStatus';
import GrowthFunnel from './GrowthFunnel';
import { acquisitionChartRows } from '../../utils/acquisitionFunnel';
import TrafficOverview from './TrafficOverview';
import { TRAFFIC_LOCALES } from '../../api/growthAnalytics';

const count = (value) => value == null ? '—' : value.toLocaleString();
const platformName = (platform) => platform === 'ios' ? 'iOS' : 'Android';
const usableData = (query) => query && !query.error ? query.data : undefined;

export function GrowthStatus({ status }) {
  const key = status || 'not_collected';
  const tone = key === 'ok' ? 'success' : ['permission_denied', 'unavailable'].includes(key) ? 'danger' : ['delayed', 'partial'].includes(key) ? 'warning' : 'neutral';
  return <span className={`admin-status-badge admin-status-${tone}`}>{t(`analytics.growth.status.${key}`)}</span>;
}

function QueryNotice({ query }) {
  const status = queryStatus(query);
  if (status === 'ok') return null;
  return <p role="status" className="mt-2 text-sm"><GrowthStatus status={status} /></p>;
}

export function StoreMetricCard({ data, platform, metricId, query }) {
  const { metric, date, daily, coverage } = storeMetric(data, platform, metricId);
  const queryState = queryStatus(query);
  const nonAggregatable = !daily && ['sum_daily_unique', 'not_aggregatable'].includes(metric?.aggregation_basis);
  const status = queryState !== 'ok' ? queryState : nonAggregatable && metric?.status === 'ok' ? 'unsupported' : metric?.status || 'not_collected';
  const value = queryState === 'ok' && !nonAggregatable ? metricNumber(metric) : null;
  return (
    <div role="group" aria-label={t(`analytics.growth.metrics.${metricId}`)}>
    <DashboardKpi label={t(`analytics.growth.metrics.${metricId}`)} value={count(value)}
      hint={`${platformName(platform)} · ${daily ? t('analytics.growth.latestDaily', { date: date || '—' }) : coverage && queryState === 'ok' ? t('analytics.growth.confirmedDays', { days: coverage.days }) : t('analytics.growth.selectedPeriod')}`}>
      <GrowthStatus status={queryState === 'ok' && coverage?.excluded ? 'partial' : status} />
      {metric && <p className="mt-1 text-xs text-dark-text-muted" title={t(`analytics.growth.basis.${metric.aggregation_basis}`)}>
        {metric.covered_from || '—'} → {metric.covered_to || '—'}
      </p>}
      {queryState === 'ok' && coverage?.excluded > 0 && <p className="mt-1 text-xs text-dark-text-secondary">{t('analytics.growth.excludedDays', { days: coverage.excluded })}</p>}
    </DashboardKpi>
    </div>
  );
}

function ReportStatusTable({ data, query }) {
  return <DashboardPanel title={t('analytics.growth.reports')} subtitle={t('analytics.growth.reportHint')}>
    <QueryNotice query={query} />
    {!!data?.reports.length && <div className="overflow-x-auto"><table className="w-full text-left text-xs text-dark-text-secondary">
      <thead><tr className="border-b border-dark-border">{['report', 'reportStatus', 'latestReport', 'timezone', 'lastSuccess', 'lastAttempt', 'delay'].map((key) => <th className="p-2" key={key}>{t(`analytics.growth.${key}`)}</th>)}</tr></thead>
      <tbody>{data.reports.map((report) => <tr className="border-b border-dark-border/50" key={`${report.report_id}:${report.partition}`}>
        <td className="p-2"><span className="text-dark-text">{report.source} · {report.report_name}</span><br />{report.partition} · {report.report_id}</td>
        <td className="p-2"><GrowthStatus status={report.status} /></td>
        <td className="p-2">{report.latest_available_date || '—'}</td>
        <td className="p-2">{report.timezone || t('analytics.growth.unknown')}</td>
        <td className="p-2">{report.last_success_at || '—'}</td>
        <td className="p-2">{report.last_attempt_at || '—'}</td>
        <td className="p-2">{report.expected_delay_days == null ? '—' : t('analytics.growth.delayDays', { days: report.expected_delay_days })}</td>
      </tr>)}</tbody>
    </table></div>}
    {queryStatus(query) === 'ok' && !data?.reports.length && <p className="py-4 text-sm text-dark-text-secondary">{t('analytics.growth.noRows')}</p>}
  </DashboardPanel>;
}

export default function GrowthTab() {
  const location = useLocation();
  const { range, preset, platform, entry, errorKey: viewError, versionRequested } = readGrowthView(location.search);
  const locale = new URLSearchParams(location.search).get('traffic_locale') ?? 'all';
  const errorKey = viewError || (!['all', ...TRAFFIC_LOCALES].includes(locale) ? 'analytics.traffic.languageInvalid' : null);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const onError = useCallback((error) => {
    if (error.status === 401) { logout(); navigate('/login', { replace: true }); }
  }, [logout, navigate]);
  const updateView = (nextRange, nextPlatform, nextPreset, nextEntry = entry) => navigate({ pathname: location.pathname, search: growthViewSearch(location.search, nextRange, nextPlatform, nextPreset, nextEntry) });
  const { byId, platforms, apiEnvironment, fetching, refresh } = useGrowthData({ ...range, platform, entry, locale, enabled: !errorKey, onError });
  const changeTrafficLocale = (next) => {
    const params = new URLSearchParams(growthViewSearch(location.search, range, platform, preset, entry));
    params.set('traffic_locale', next);
    navigate({ pathname: location.pathname, search: `?${params}` });
  };
  const store = usableData(byId.store);
  const chart = errorKey ? [] : growthChartRows(range.from, range.to, store);
  const acquisitionChart = errorKey ? [] : acquisitionChartRows(range.from, range.to, byId.landing, byId.signups);
  const downloadLines = useMemo(() => platforms.map((appPlatform) => ({
    dataKey: `${appPlatform}Downloads`, name: t(`analytics.growth.metrics.${STORE_METRICS[appPlatform][0]}`),
    color: appPlatform === 'ios' ? 'rgb(var(--admin-chart-blue))' : 'rgb(var(--admin-chart-green))',
  })), [platforms]);
  const visitLines = useMemo(() => platforms.map((appPlatform) => ({
    dataKey: `${appPlatform}Visits`, name: t(`analytics.growth.metrics.${STORE_METRICS[appPlatform][1]}`),
    color: appPlatform === 'ios' ? 'rgb(var(--admin-chart-purple))' : 'rgb(var(--admin-chart-orange))',
  })), [platforms]);
  const displayedMetrics = platforms.flatMap((appPlatform) => STORE_METRICS[appPlatform]);
  const reportIds = new Set();
  store?.summary?.forEach((row) => row.metrics?.forEach((metric) => {
    if (displayedMetrics.includes(metric.metric_id) && metric.report_id) reportIds.add(metric.report_id);
  }));
  store?.daily?.forEach((row) => row.metrics?.forEach((metric) => {
    if (displayedMetrics.includes(metric.metric_id) && metric.report_id) reportIds.add(metric.report_id);
  }));
  const reports = store?.reports.filter((report) => reportIds.has(report.report_id)) || [];
  const displayedStore = store ? { ...store, reports, metric_definitions: store.metric_definitions.filter((definition) => displayedMetrics.includes(definition.metric_id)) } : undefined;
  const partial = !byId.store.isPending && (byId.store.error || reports.some((report) => report.status !== 'ok') || platforms.some((appPlatform) => STORE_METRICS[appPlatform].some((metricId) => storeMetric(store, appPlatform, metricId).metric?.status !== 'ok')));
  return <section className="space-y-4" aria-labelledby="growth-title">
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div><h2 id="growth-title" className="text-xl font-bold text-dark-text">{t('analytics.tabs.growth')}</h2>
        <p className="mt-1 text-sm text-dark-text-secondary">{t('analytics.growth.intro')}</p>
        <p className="mt-1 text-xs text-dark-text-muted">{apiEnvironment.toUpperCase()} · {t('analytics.growth.dateSelection')}</p></div>
      <button type="button" onClick={refresh} disabled={fetching || Boolean(errorKey)} className="inline-flex items-center gap-2 rounded-lg border border-dark-border px-3 py-2 text-sm text-dark-text disabled:opacity-50"><RefreshCw className={`h-4 w-4 ${fetching ? 'animate-spin' : ''}`} />{t('dashboard.refresh')}</button>
    </div>
    <div className="flex flex-wrap items-end gap-4">
      <GaDateRangeControls preset={preset} appliedRange={range} getPresetRange={getGrowthDateRange} maxDate={getGrowthDateRange(1).to} validateRange={validateGrowthRange}
        onApply={({ preset: nextPreset, range: nextRange }) => updateView(nextRange, platform, nextPreset)} />
      <label className="flex flex-col gap-1 text-xs text-dark-text-secondary">{t('analytics.growth.platform')}<select value={platform} onChange={(event) => updateView(range, event.target.value, preset)} className="rounded-lg border border-dark-border bg-dark-card px-3 py-2 text-sm text-dark-text">
        <option value="all">{t('analytics.growth.allApps')}</option><option value="ios">iOS</option><option value="android">Android</option>
      </select></label>
      <label className="flex flex-col gap-1 text-xs text-dark-text-secondary">{t('analytics.acquisition.entryFilter')}<select value={entry} onChange={(event) => updateView(range, platform, preset, event.target.value)} className="rounded-lg border border-dark-border bg-dark-card px-3 py-2 text-sm text-dark-text">
        {['landing', 'home'].map((value) => <option key={value} value={value}>{t(`analytics.acquisition.entry.${value}`)}</option>)}
      </select></label>
    </div>
    <p className="text-xs text-dark-text-muted">{t('analytics.acquisition.entryDefinition')}</p>
    <p className="text-xs text-dark-text-muted">{t('analytics.acquisition.entryScope')}</p>
    <p className="text-xs text-dark-text-muted">{t(versionRequested ? 'analytics.growth.versionNotApplied' : 'analytics.growth.versionScope')}</p>
    {errorKey && <p role="alert" className="admin-notice admin-notice-error">{t(errorKey)} {t('analytics.growth.sharedRangeInvalid')}</p>}
    {!errorKey && <>
    <TrafficOverview query={byId.traffic} entry={entry} locale={locale} onLocaleChange={changeTrafficLocale} />
    {partial && <p role="status" className="admin-notice" style={{ color: 'rgb(var(--admin-warning))' }}>{t('analytics.growth.partial')}</p>}
    <GrowthFunnel byId={byId} platforms={platforms} range={range} entry={entry}
      renderStoreCard={(appPlatform, metricId) => <StoreMetricCard data={store} platform={appPlatform} metricId={metricId} query={byId.store} />} />
    <div className="grid gap-3 xl:grid-cols-2">
      <GaMetricLineChart title={t('analytics.acquisition.dailyLanding')} data={acquisitionChart} isLoading={byId.landing?.isPending} heightClass="h-64" showPoints lines={[
        { dataKey: 'landing', name: t('analytics.acquisition.landingSessions'), color: 'rgb(var(--admin-chart-blue))' },
        { dataKey: 'iosClicks', name: t('analytics.acquisition.iosClicks'), color: 'rgb(var(--admin-chart-purple))' },
        { dataKey: 'androidClicks', name: t('analytics.acquisition.androidClicks'), color: 'rgb(var(--admin-chart-green))' },
      ]} />
      <GaMetricLineChart title={t('analytics.acquisition.dailySignups')} data={acquisitionChart} isLoading={byId.signups?.isPending} heightClass="h-64" showPoints lines={[
        { dataKey: 'signups', name: t('analytics.acquisition.signups'), color: 'rgb(var(--admin-chart-orange))' },
      ]} />
    </div>
    <p className="text-xs text-dark-text-muted">{t('analytics.acquisition.timezoneHint')}</p>
    <div>
      {!byId.store.error && <QueryStatus query={byId.store} source={t('analytics.growth.storeReports')} />}
      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-xs text-dark-text-secondary">
        {reports.map((report) => <p key={`${report.report_id}:${report.partition}`}>{report.source} · {report.report_name} · {report.partition}: {report.latest_available_date || '—'} · <GrowthStatus status={report.status} /></p>)}
      </div>
    </div>
    <GaMetricLineChart title={t('analytics.growth.dailyDownloads')} data={chart} isLoading={byId.store.isPending} heightClass="h-64" showPoints lines={downloadLines} />
    <p className="text-xs text-dark-text-muted">{t('analytics.growth.visitHint')}</p>
    <GaMetricLineChart title={t('analytics.growth.dailyVisits')} data={chart} isLoading={byId.store.isPending} heightClass="h-64" showPoints lines={visitLines} />
    <details className="rounded-lg border border-dark-border bg-dark-card p-3">
      <summary className="cursor-pointer text-sm font-semibold text-dark-text">{t('analytics.growth.dataSources')}</summary>
      <div className="mt-3"><ReportStatusTable data={displayedStore} query={byId.store} /></div>
    </details>
    {!!displayedStore?.metric_definitions.length && <details className="rounded-lg border border-dark-border bg-dark-card p-3">
      <summary className="cursor-pointer text-sm font-semibold text-dark-text">{t('analytics.growth.definitions')}</summary>
      <div className="mt-3">
      <dl className="space-y-3 text-xs">{displayedStore.metric_definitions.map((definition) => <div key={definition.metric_id}>
        <dt className="font-semibold text-dark-text">{t(`analytics.growth.metrics.${definition.metric_id}`)} · {definition.native_metric} ({definition.unit})</dt>
        <dd className="mt-1 text-dark-text-secondary">{definition.definition} · {t('analytics.growth.dailyUnique')}: {String(definition.daily_unique)} · {definition.period_aggregation}</dd>
      </div>)}</dl>
      </div>
    </details>}
    <nav className="admin-context-meta" aria-label={t('analytics.growth.investigate')}>
      <Link to={`/analytics/events${growthViewSearch(location.search, range, platform, preset)}`}>{t('header.nav.analyticsEvents')}</Link>
      <Link to={`/products/story${growthViewSearch(location.search, range, platform === 'all' ? '' : platformName(platform), preset)}`}>{t('header.nav.products')}</Link>
    </nav>
    </>}
  </section>;
}
